/* eslint-disable import/order */
import { immutableSelector } from '@/utils/index'
import { ClockCircleOutlined, CopyOutlined, FormOutlined } from '@ant-design/icons'
import T from '@watchface/components/I18n'
import CreateForm from '@watchface/pages/Create'
import { message, Modal, Popover, Tooltip } from 'antd'
import type { Map as ImmutableMap } from 'immutable'
import Immutable from 'immutable'
import React, { useRef, useState } from 'react'
import './styles/WatchInfo.scss'

interface WatchInfoProps {
  metas: ImmutableMap<string, any>
  support: ImmutableMap<string, any>
  availableDeviceNames: any
  watchConfig: ImmutableMap<string, any>
  loading: boolean
  asset: ImmutableMap<string, any>
}

const WatchInfo = (props: WatchInfoProps): JSX.Element | null => {
  const { watchConfig, loading, metas, support, asset, availableDeviceNames } = props
  const dialName = (metas || Immutable.Map()).getIn(['multi_language', 0, 'name']) as string
  const { series } = immutableSelector(support)
  const apiLevel = asset.get('apiLevel')
  const appId = asset.get('appId')
  const safeDeviceNames = Array.isArray(availableDeviceNames)
    ? availableDeviceNames.filter((deviceName: string, index: number) => availableDeviceNames.indexOf(deviceName) === index)
    : []
  const [state, _setState] = useState<any>({
    modifyModalVisible: false
  })
  const stateRef: any = useRef(state)
  const setState = (state: any) => {
    stateRef.current = state
    _setState(state)
  }
  const { modifyModalVisible } = state
  const { screenWidth, screenHeight } = immutableSelector(watchConfig)
  const sizeText = screenWidth && screenHeight ? `${screenWidth}×${screenHeight}` : '--'
  const deviceCount = safeDeviceNames.length

  if (loading) return null

  const setEditModalVisible = (visible = false) => {
    setState({
      ...state,
      modifyModalVisible: visible
    })
  }

  const handleCopyAppId = async () => {
    if (!appId) return

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(appId))
      } else {
        const input = document.createElement('input')
        input.value = String(appId)
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }
      message.success('appId copied')
    } catch (err) {
      message.error('Copy appId failed')
    }
  }

  const devicePopoverContent = (
    <div className="watch-info-device-list">
      {(safeDeviceNames.length ? safeDeviceNames : ['--']).map((deviceName: string) => (
        <Tooltip key={deviceName} title={deviceName} placement="right">
          <div className="watch-info-device-item">{deviceName}</div>
        </Tooltip>
      ))}
    </div>
  )

  return (
    <div className="watch-info">
      <span className="watch-info-name">{dialName || <T id="no_name" />}</span>
      <Tooltip title={<T id="modify_dial_info" />}>
        <button className="watch-info-btn" type="button" onClick={() => setEditModalVisible(true)}>
          <FormOutlined />
        </button>
      </Tooltip>
      <span className="watch-info-divider" />
      <span className="watch-info-chip">{sizeText}</span>
      {series === 'js' && apiLevel ? <span className="watch-info-chip watch-info-chip-accent">API LEVEL {apiLevel}</span> : null}
      <span className="watch-info-divider" />
      <span className="watch-info-meta-group">
        <span className="watch-info-meta-label">appId</span>
        <span className="watch-info-meta-value">{appId || <T id="no_data" />}</span>
        {appId ? (
          <button className="watch-info-btn" type="button" onClick={handleCopyAppId}>
            <CopyOutlined />
          </button>
        ) : null}
      </span>
      {safeDeviceNames.length ? (
        <Popover
          content={devicePopoverContent}
          title={
            <>
              {deviceCount} <T id="api_level_support_devices" />
            </>
          }
          trigger="click"
          placement="bottom"
          overlayClassName="watch-info-device-popover"
        >
          <button className="watch-info-secondary-btn" type="button">
            <ClockCircleOutlined className="watch-info-secondary-btn-icon" />
            <T id="support_device" />
            <span className="watch-info-secondary-btn-badge">{deviceCount}</span>
          </button>
        </Popover>
      ) : null}
      <Modal
        footer={null}
        title={<T id="modify_dial_info" />}
        visible={modifyModalVisible}
        closeIcon={<span />}
        centered
        destroyOnClose
        className="watch-dial-modal"
        width={800}
      >
        <CreateForm
          key={modifyModalVisible}
          handleCancel={() => setEditModalVisible(false)}
          handleClose={() => setEditModalVisible(false)}
          isEdit
          device={support}
          metas={metas}
        />
      </Modal>
    </div>
  )
}

export default WatchInfo
